import { ErrorBoundary } from '@sentry/react';
import { ButtonIcon } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { Dispatch, SetStateAction, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { visibleTableColumnsAtom } from '@/atoms/table';
import { EditTableColumnsModal } from '../modal/EditTableColumnsModal/EditTableColumnsModal';

const EditColumnsButtonBase = ({
  disabled,
  visibleColumnsCount,
  setShowEditColumnsModal,
}: {
  disabled?: boolean;
  visibleColumnsCount?: number;
  setShowEditColumnsModal?: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation(['table']);

  return (
    <ButtonIcon
      size="small"
      icon="Visibility"
      displayText="right"
      variant="ghost"
      ariaLabel={visibleColumnsCount ? `${t('editColumns')} (${visibleColumnsCount})` : t('editColumns')}
      onClick={() => setShowEditColumnsModal && setShowEditColumnsModal(true)}
      disabled={disabled}
    >
      {/* ButtonIcon.children is typed as string but accepts ReactNode at runtime;
          ariaLabel is set explicitly so ButtonIcon does not derive it from this fragment */}
      {
        (
          <>
            <span className="hidden xl:inline">{t('editColumns')}</span>
            {visibleColumnsCount ? <span className="hidden xl:inline">{` (${visibleColumnsCount})`}</span> : null}
          </>
        ) as unknown as string
      }
    </ButtonIcon>
  );
};

const EditColumnsButtonWithData = () => {
  const [showEditColumnsModal, setShowEditColumnsModal] = useState(false);
  const visibleColumnsCount = useAtomValue(visibleTableColumnsAtom).length;
  const isDisabled = visibleColumnsCount === 0;

  return (
    <>
      <EditColumnsButtonBase
        visibleColumnsCount={visibleColumnsCount}
        setShowEditColumnsModal={setShowEditColumnsModal}
        disabled={isDisabled}
      />
      {showEditColumnsModal && (
        <ErrorBoundary>
          <Suspense>
            <EditTableColumnsModal setShowEditColumnsModal={setShowEditColumnsModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

export const EditColumnsButton = () => {
  const visibleTableColumnCount = useAtomValue(visibleTableColumnsAtom).length;

  return (
    <ErrorBoundary fallback={<EditColumnsButtonBase visibleColumnsCount={visibleTableColumnCount} disabled />}>
      <Suspense fallback={<EditColumnsButtonBase visibleColumnsCount={visibleTableColumnCount} disabled />}>
        <EditColumnsButtonWithData />
      </Suspense>
    </ErrorBoundary>
  );
};
